import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { VideoPlayer } from '../../components/VideoPlayer';
import './share-viewer.css';

interface ShareInfo {
  shareId: string;
  fileKey: string;
  permission: 'read' | 'read-write';
  createdAt: string;
}

interface FileInfo {
  key: string;
  size: number;
  contentType: string;
}

export const ShareViewer = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchShareInfo = async () => {
      try {
        const response = await fetch(`/api/share/${shareId}`);
        if (!response.ok) {
          throw new Error('Share link not found or has expired');
        }
        const data = await response.json();
        setShareInfo(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load share');
        setIsLoading(false);
      }
    };

    if (shareId) {
      fetchShareInfo();
    }
  }, [shareId]);

  useEffect(() => {
    if (!shareInfo) return;

    const fetchFileInfo = async () => {
      try {
        // Get file info by listing files with the specific key
        const response = await fetch('/api/files');
        if (response.ok) {
          const data = await response.json();
          const file = data.files.find((f: any) => f.key === shareInfo.fileKey);
          if (file) {
            setFileInfo(file);
          }
        }
      } catch (err) {
        console.error('Error loading file info:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFileInfo();
  }, [shareInfo]);

  if (isLoading) {
    return (
      <div className="share-viewer-container">
        <div className="share-viewer-loading">
          <div className="loading-spinner"></div>
          <p>Loading shared file...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="share-viewer-container">
        <div className="share-viewer-error">
          <div className="error-icon">🔒</div>
          <h2>Share Link Not Found</h2>
          <p>{error}</p>
          <a href="/" className="back-link">← Back to Home</a>
        </div>
      </div>
    );
  }

  if (!shareInfo || !fileInfo) {
    return (
      <div className="share-viewer-container">
        <div className="share-viewer-error">
          <div className="error-icon">❌</div>
          <h2>File Not Found</h2>
          <p>The file associated with this share link could not be found.</p>
          <a href="/" className="back-link">← Back to Home</a>
        </div>
      </div>
    );
  }

  const isVideo = fileInfo.contentType.startsWith('video/');
  const isImage = fileInfo.contentType.startsWith('image/');
  const canDownload = shareInfo.permission === 'read' || shareInfo.permission === 'read-write';

  return (
    <div className="share-viewer-container">
      <div className="share-viewer-content">
        {/* Share Info Header */}
        <div className="share-header">
          <div className="share-header-content">
            <h1>Shared File</h1>
            <p className="share-info-text">
              📁 {fileInfo.key} • {formatFileSize(fileInfo.size)}
            </p>
            <div className="share-permissions">
              {shareInfo.permission === 'read' && (
                <span className="permission-badge read">👁️ View Only</span>
              )}
              {shareInfo.permission === 'read-write' && (
                <span className="permission-badge read-write">✏️ View & Download</span>
              )}
            </div>
          </div>
          <a href="/" className="share-home-link">Home</a>
        </div>

        {/* File Viewer */}
        <div className="share-viewer-area">
          {isVideo ? (
            <div className="share-video-wrapper">
              <VideoPlayer
                src={`/api/files/download/${encodeURIComponent(fileInfo.key)}`}
                title={fileInfo.key}
                aspectRatio="16 / 9"
                className="share-video-player"
              />
            </div>
          ) : isImage ? (
            <div className="share-image-wrapper">
              <img
                src={`/api/files/download/${encodeURIComponent(fileInfo.key)}`}
                alt={fileInfo.key}
                className="share-image"
              />
            </div>
          ) : (
            <div className="share-file-info">
              <div className="file-icon">📄</div>
              <h2>{fileInfo.key}</h2>
              <p>Type: {fileInfo.contentType}</p>
              <p>Size: {formatFileSize(fileInfo.size)}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {canDownload && (
          <div className="share-actions">
            <a
              href={`/api/files/download/${encodeURIComponent(fileInfo.key)}`}
              download={fileInfo.key}
              className="download-btn"
            >
              ⬇️ Download File
            </a>
          </div>
        )}

        {/* Footer Info */}
        <div className="share-footer">
          <p className="share-date">
            Shared on {new Date(shareInfo.createdAt).toLocaleDateString()}
          </p>
          <p className="share-note">
            ℹ️ This is a shared link. No login required to access this file.
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export default ShareViewer;
